import { SendingOptionModel } from '@products/models/product-selection.model';

export type OrderUuid = string;

export interface FieldSetting {
  isVisible: boolean;
  isMandatory: boolean;
  isReadOnly?: boolean;
  order: number;
}

export interface FieldSettings {
  Salutation: FieldSetting;
  PersonTitle: FieldSetting;
  FirstName: FieldSetting;
  LastName: FieldSetting;
  DateOfBirth: FieldSetting;
  Street: FieldSetting;
  ZipCode: FieldSetting;
  City: FieldSetting;
  Company: FieldSetting;
  PersonFunction: FieldSetting;
  Department: FieldSetting;
  OccupationalGroup: FieldSetting;
  Phone: FieldSetting;
  Country: FieldSetting;
  Email: FieldSetting;
  SearchAddress: FieldSetting;
  MailConfirm: FieldSetting;
}

export interface TicketOwnerSettingsModel {
  fieldSettings: FieldSettings;
  //ticketSendingMode: string;
}

export interface TariffSpecificSettings {
  ticketOwnerSettings: TicketOwnerSettingsModel;
  ticketPersonId: number;
}

export interface QuestionnaireDataSection {
  id: number;
  values: Array<QuestionnaireDataInput>;
  label: string;
  name: string;
  type: string;
  order: number;
  isMandatory: boolean;
  textLength: number;
  selectionLimit: number;
  previousValueId?: number;
}

export interface QuestionnaireDataInput {
  id: number;
  text: string;
  order: number;
}

export interface IFieldSettings {
  MailConfirm: FieldSetting;
  SearchAddress: FieldSetting;
  VatNumber: FieldSetting;
  Country: FieldSetting;
  Salutation: FieldSetting;
  PersonTitle: FieldSetting;
  FirstName: FieldSetting;
  LastName: FieldSetting;
  DateOfBirth: FieldSetting;
  Street: FieldSetting;
  ZipCode: FieldSetting;
  City: FieldSetting;
  Company: FieldSetting;
  // CompanyAccount: FieldSetting;
  PersonFunction: FieldSetting;
  Department: FieldSetting;
  OccupationalGroup: FieldSetting;
  // AccountFunction: FieldSetting;
  Phone: FieldSetting;
  Email: FieldSetting;
}

export interface IWithFieldSetting {
  fieldSettings: IFieldSettings;
}

export const enum NewsletterInputType {
  Hidden = 'hidden',
  CheckboxUncheckedByDefault = 'checkboxUncheckedByDefault',
  CheckboxCheckedByDefault = 'checkboxCheckedByDefault',
  RadioButtonUnselected = 'radioButtonUnselected'
}

export enum UniqueVisitorCheckType {
  IgnoreDuplicates = 'ignoreDuplicates',
  WarnIfDuplicate = 'warnIfDuplicate',
  MustBeUnique = 'mustBeUnique'
}

export interface BuyerSettings extends IWithFieldSetting {
  enableDifferentBillingAddress: boolean;
  isReadonlyBuyerAndAccountInfo: boolean;
  newsletterInputType: NewsletterInputType;
  showFairCatalogCheckbox: boolean;
  showCreateAccountCheckbox: boolean;
  fairCatalogDefaultValue: boolean;
}

export type RegistrationSettings = IWithFieldSetting;

export interface ExhibitionSettingModel {
  id: number;
  eventId: number;
  ageRating: number;
  workshopSeatsReleaseInMinutes?: number;
  ticketReleaseInMinutes?: number;
  currency?: string;
  languages: Array<string>;
  eventBanner: string;
  hideHeaderLogoArea: boolean;
  eventSerie: string;
  eventLogo: string;
  hasSponsorArea: boolean;
  promoCodeVisible: PromoCodeVisibility.Hidden | PromoCodeVisibility.AlwaysVisible | PromoCodeVisibility.AfterLogin;
  loginMode: string;
  limitBoughtTickets: number;
  limitPromoCodes: number;
  limitLimitedPromocodes: number;
  tradeVisitorLegitimation: boolean;
  mobileTicket: boolean;
  twintPayment: boolean;
  goToLegitimationForNewAccount: boolean;
  emailIsMandatoryForMobileTicket: boolean;
  enableLastNameForForgottenPassword: boolean;
  isTermsVisible: boolean;
  isTermsMandatory: boolean;
  termsUrl: string;
  isPrivacyLinkVisible: boolean;
  isPrivacyLinkMandatory: boolean;
  isPrivacyOptionalVisible: boolean;
  isPrivacyOptionalMandatory: boolean;
  isSponsorBannerUrlVisible: boolean;
  isSponsorLogoUrlVisible: boolean;
  sponsorBannerUrl: string;
  sponsorLogoUrl: string;
  privacyUrl: string;
  privacyOptionalUrl: string;
  isDisclaimerVisible: boolean;
  disclaimerUrl: string;
  isImprintVisible: boolean;
  imprintUrl: string;
  isGoogleAutocompleteEnabled: boolean;
  faxNumber: string;
  isTelephoneValidationDisabled: boolean;
  buyerSettings: BuyerSettings;
  tariffSpecificSettings: TariffSpecificSettings[];
  ticketOwnerSettings: TicketOwnerSettingsModel;
  registrationSettings: RegistrationSettings;
  ticketSelectionDeliveryTypes: TicketSelectionDeliveryType[];
  customStyles: ExhibitionCustomStyles;
  customStylesId: number;
  isContinueAsGuestEnabled: boolean;
  isTicketHolderVisible?: boolean;
  isWorkshopsSelectionMandatory: boolean;
  workshopsPerTicket: number;
  maintenance: MaintenanceModel;
  currencySettings?: CurrencySettingsModel;
  isOptionalLegitimationCommentAllowed?: boolean;
  isLegitimationWithCommentAllowed?: boolean;
  isLegitimationWithFaxAllowed?: boolean;
  isLegitimationWithFileAllowed?: boolean;
  isLegitimationWithUrlAllowed?: boolean;
  uniqueVisitorCheckType: UniqueVisitorCheckType;
  isDownloadTicketButtonVisible?: boolean;
  localTime: string;
  showAmountOfAvailableWorkshops: boolean;
  showWorkshopsForSelectedTicketsOnly: boolean;
  workshopMandatoryForZeroPriceTickets: boolean;
  hideWorkshopDate: boolean;
  enableWorkshopSelectionOverlapping: boolean;
  workshopsOnTicketSelection: boolean;
  workshopViewType: 'calendar' | 'list';
  paymentMethods: ExhibitorSettingsPaymentMethod[];
  ticketLimitPerEmail: number;
  hideWorkshopPrice: boolean;
  isBannerLinkVisible: boolean;
  bannerLink: string;
  isHeaderLogoLinkVisible: boolean;
  headerLogoLink: string;
  ticketLimitPerUserAccount: number;
  isVoucherIncludedPerUserAccountLimit: boolean;
  isPromoCodeEnabled: boolean;
  initiateConteoLogin: boolean;
}

export type TicketSelectionDeliveryType = Omit<SendingOptionModel, 'isVisible'>;

export const enum PromoCodeVisibility {
  Hidden = 'hidden',
  AlwaysVisible = 'alwaysVisible',
  AfterLogin = 'afterLogin'
}

export interface PrivacyPolicyCheckbox {
  checkboxKey: string;
  optionKey: string;
  value: boolean;
  label: string;
  isRequired: boolean;
  text?: {
    value: string;
    translation: string;
    position: 'prepend' | 'append';
  };
}

export class ExhibitorSettingsPaymentMethod {
  public id: string;
  public name: string;
}

export interface CurrencySettingsModel {
  currencyCode: string;
  decimalMark: string;
  thousandsSeparator: string;
}

export interface OperatorsSettingsModel {
  languages: Array<string>;
  defaultLanguage: string;
  operatorBanner: string;
  operatorLogo: string;
  enableLastNameForForgottenPassword: boolean;
  newsletterInputType: NewsletterInputType;
  isTelephoneValidationDisabled: boolean;
  customStyles: ExhibitionCustomStyles;
  loginMode?: string;
  customStylesId: number;
  maintenance: MaintenanceModel;
  dateFormats: { [key: string]: string };
  registrationSettings: RegistrationSettings;
  ticketOwnerSettings: TicketOwnerSettingsModel;
  isImprintVisible: boolean;
  imprintUrl: string;
  isPrivacyLinkVisible: boolean;
  privacyUrl: string;
  isTermsVisible: boolean;
  termsUrl: string;
  isBannerLinkVisible: boolean;
  isHeaderLogoLinkVisible: boolean;
  bannerLink: string;
  headerLogoLink: string;
  isReadonlyBuyerAndAccountInfo: boolean;
  useGoogleMapsAPI: boolean;
  isSIAEEnabled: boolean;
  googleReCaptchaSiteKey: string;
  isNF525Enabled: boolean;
}

export interface MaintenanceModel {
  isPlanned: boolean;
  isPresent: boolean;
  reason: string;
  since: Date;
  until: Date;
}

export interface SponsorBannerModel {
  sponsorBanner: string;
}

export interface CssSkinModel {
  priority: number;
  css: string;
  variables: ExhibitionCustomStyles;
  id?: number;
  title?: string;
}

export interface LocalizedImagesModel {
  banner: string;
  logo: string;
  background: string;
  favicon: string;
}

export interface ExhibitionCustomStyles {
  id: number;
  VoucherBlockBgColor: string;
  VoucherRedeemButtonBgColor: string;
  VoucherRedeemButtonBorderColor: string;
  VoucherRedeemButtonTxColor: string;
  VoucherRedeemButtonHoverBgColor: string;
  VoucherRedeemButtonHoverTxColor: string;
  VoucherTariffBgColor: string;
  VoucherTariffBorderColor: string;
  VoucherTariffNameTxColor: string;
  VoucherTariffPriceTxColor: string;
  VoucherTariffDescriptionTxColor: string;
  VoucherTariffVoucherCodeBgColor: string;
  VoucherTariffVoucherCodeColor: string;
  VoucherTariffRemoveIconBgColor: string;
  VoucherTariffRemoveIconColor: string;
  PromoCodeDiscountColor: string;
  PromoCodePriceColor: string;
  PromoCodeDiscountedPriceColor: string;
  PromoCodeBgColor: string;
  PromoCodeColor: string;
  PromoCodeRemoveIconBgColor: string;
  PreferredTariffBgColor: string;
  PreferredTariffBorderColor: string;
  PreferredTariffNameTxColor: string;
  PreferredTariffPriceTxColor: string;
  PreferredTariffDescriptionTxColor: string;
  TicketOwnerEmailListTxColor: string;
  ErrorTxColor: string;
  ErrorHdrColor: string;
  ErrorBgColor: string;
  HeadlineColor: string;
  HeadlineDescriptionColor: string;
  ThemeHeadingTxColor: string;
  ThemeHeadingBgColor: string;
  ThemeTxColor: string;
  ThemeLinkTxColor: string;
  TariffTypeBgColor: string;
  TariffTypeNameTxColor: string;
  TariffBgColor: string;
  TariffBorderColor: string;
  TariffNameTxColor: string;
  TariffDescriptionTxColor: string;
  TariffWarningTxColor: string;
  TariffPriceTxColor: string;
  WorkshopHeadingTxColor: string;
  WorkshopDateTxColor: string;
  WorkshopNameTxColor: string;
  WorkshopPriceTxColor: string;
  WorkshopDetailsTxColor: string;
  WorkshopDescriptionTxColor: string;
  WorkshopSoldOutWarningTxColor: string;
  ProductPriceTxColor: string;
  RequiredErrLabelTxColor: string;
  RequiredErrTxColor: string;
  RequiredErrBrdColor: string;
  RadioLabelTxColor: string;
  RadioBorder: string;
  RadioCheckedColor: string;
  RadioBgColor: string;
  BorderedRadioBgColor: string;
  BorderedRadioBorderColor: string;
  BorderedRadioCheckedBgColor: string;
  BorderedRadioCheckedBorderColor: string;
  DropdownActiveTxtColor: string;
  DropdownActiveBgColor: string;
  SectionContentBgColor: string;
  SectionContentHeadingTxColor: string;
  ValidInputLabelColor: string;
  InputBorderColor: string;
  InputBorderHoverColor: string;
  InputBorderFocusColor: string;
  InfoMessageTxColor: string;
  InfoMessageBgColor: string;
  InfoMessageBorderColor: string;
  HeaderNavTxColor: string;
  HeaderNavBgColor: string;
  HeaderNavBorderColor: string;
  HeaderNavActiveLinkTxColor: string;
  BannerEventInfoTxColor: string;
  BannerEventInfoBgColor: string;
  CheckboxPolicyTxColor: string;
  CheckboxPolicyTxBgColor: string;
  CheckboxPolicyTxBorderColor: string;
  CheckboxLabelTxColor: string;
  CheckboxBgColor: string;
  CheckboxBorderColor: string;
  CheckboxCheckMarkColor: string;
  CheckboxErrorBorderColor: string;
  ConfirmationVoucherTxColor: string;
  ConfirmationDropdownBorderColor: string;
  ConfirmationDropdownTxColor: string;
  ConfirmationDropdownHoverTxColor: string;
  ConfirmationDropdownHoverBgColor: string;
  ConfirmationTariffBgColor: string;
  ConfirmationTariffTotalPriceBgColor: string;
  ConfirmationTariffWorkshopsIconTitleColor: string;
  ConfirmationTariffWorkshopBgColor: string;
  ConfirmationProductNameTxColor: string;
  ConfirmationProductPriceTxColor: string;
  ConfirmationTariffPersonTypeTxColor: string;
  ConfirmationTariffHolderNameTxColor: string;
  ConfirmationTariffHolderEmailTxColor: string;
  ConfirmationTariffHolderInfoBorderColor: string;
  ConfirmationPaymentTxColor: string;
  ConfirmationPaymentHoverTxColor: string;
  ConfirmationPaymentBgColor: string;
  ConfirmationPaymentHoverBgColor: string;
  ButtonOutlineColor: string;
  SplitButtonPrimaryBgColor: string;
  SplitButtonPrimaryHoverBgColor: string;
  SplitButtonPrimaryTxColor: string;
  SplitButtonPrimaryHoverTxColor: string;
  SelectButtonActiveBgColor: string;
  SelectButtonActiveTxColor: string;
  SelectButtonActiveHoverBgColor: string;
  SelectButtonActiveHoverTxColor: string;
  SelectButtonInactiveBgColor: string;
  SelectButtonInactiveBorderColor: string;
  SelectButtonInactiveTxColor: string;
  SelectButtonInactiveHoverBgColor: string;
  SelectButtonInactiveHoverBorderColor: string;
  SelectButtonInactiveHoverTxColor: string;
  SwitchButtonActiveBgColor: string;
  SwitchButtonActiveDisabledBgColor: string;
  SwitchButtonActiveBorderColor: string;
  SwitchButtonActiveDisabledBorderColor: string;
  SwitchButtonActiveButtonBgColor: string;
  SwitchButtonActiveDisabledButtonBgColor: string;
  SwitchButtonInactiveBgColor: string;
  SwitchButtonInactiveDisabledBgColor: string;
  SwitchButtonInactiveBorderColor: string;
  SwitchButtonInactiveDisabledBorderColor: string;
  SwitchButtonInactiveButtonBgColor: string;
  SwitchButtonInactiveDisabledButtonBgColor: string;
  ButtonPrimaryBgColor: string;
  ButtonPrimaryBorderColor: string;
  ButtonPrimaryTxColor: string;
  ButtonPrimaryHoverBgColor: string;
  ButtonPrimaryHoverBorderColor: string;
  ButtonPrimaryHoverTxColor: string;
  ButtonSecondaryBgColor: string;
  ButtonSecondaryBorderColor: string;
  ButtonSecondaryTxColor: string;
  ButtonSecondaryHoverBgColor: string;
  ButtonSecondaryHoverBorderColor: string;
  ButtonSecondaryHoverTxColor: string;
  ProductCounterButtonTxColor: string;
  ProductCounterButtonBgColor: string;
  ProductCounterButtonBorderColor: string;
  ProductCounterSpinnerColor: string;
  SelfRegToggleProductColor: string;
  SelfRegToggleProductDisabledColor: string;
  StepNavigationBgColor: string;
  AreaBlockDash: string;
  AreaBlockBgActive: string;
  AreaBlockTxActive: string;
  AreaBlockBgFutureActive: string;
  AreaBlockTxtFutureActive: string;
  AreaBlockBgFuture: string;
  AreaBlockTxFuture: string;
  AreaBlockBgInactive: string;
  AreaBlockTxInactive: string;
  LoaderColor: string;
  ThemeColor: string;
  EventSelectionHeadingTxColor: string;
  EventSelectionSubheadingTxColor: string;
  EventTitleName: string;
  EventDuration: string;
  PackageBorderColor: string;
  PackageHeaderBgColor: string;
  PackageHeaderTxColor: string;
  PackageWarningTxColor: string;
  PackageWarningBgColor: string;
  PackageWarningBorderColor: string;
  PackageProductBgColor: string;
  PackageTariffTypeNameTxColor: string;
  PackageTariffNameTxColor: string;
  PackageTariffDescriptionTxColor: string;
  PackageTariffBorderColor: string;
  PackageTariffWarningTxColor: string;
  PackageTariffPriceTxColor: string;
  PackagePriceBgColor: string;
  PackagePriceTxColor: string;
  PackageFooterBgColor: string;
  PackageFooterTxColor: string;
  TabActiveBg: string;
  TabActiveText: string;
  TabAreaBg: string;
  TabAreaBorderColor: string;
  TabAreaHeadingTxColor: string;
  TabIconColor: string;
  TabActiveIconColor: string;
  ProductGroupBgColor: string;
  ProductGroupNameTxColor: string;
  ProductGroupDescriptionTxColor: string;
  DatePickerHighlightBgColor: string;
  DatePickerHighlightTxColor: string;
  DatePickerDisabledHighlightBgColor: string;
  DatePickerDisabledHighlightTxColor: string;
  DatePickerHoverHighlightBgColor: string;
  DatePickerHoverHighlightTxColor: string;
  SendingOptionsBgColor: string;
  SendingOptionsHeadingColor: string;
  StickyFooterTxColor: string;
  StickyFooterBgColor: string;
  StickyFooterBorderColor: string;
  FooterBackgroundColor: string;
  FooterBorderColor: string;
  FooterLegalLinksTextColor: string;
  BasketCountTxColor: string;
  BasketCountBgColor: string;
  BasketButtonDividerColor: string;
  BuyerOrderOverviewHeadingColor: string;
  BuyerOrderOverviewTariffTypeColor: string;
  BuyerOrderOverviewTariffNameColor: string;
  BuyerOrderOverviewTariffVoucherCodeColor: string;
  BuyerOrderOverviewTariffPriceColor: string;
  BuyerOrderOverviewSubtotalTxColor: string;
  BuyerOrderOverviewSubtotalPriceColor: string;
  AssignmentAddTicketHolderSelectTextColor: string;
  AssignmentOrderOverviewBgColor: string;
  AssignmentOrderOverviewHeadingColor: string;
  AssignmentOrderOverviewValidBgColor: string;
  AssignmentOrderOverviewValidBorderColor: string;
  AssignmentOrderOverviewValidSelectedBorderColor: string;
  AssignmentOrderOverviewValidIconColor: string;
  AssignmentOrderOverviewValidTariffTypeColor: string;
  AssignmentOrderOverviewValidTariffNameColor: string;
  AssignmentOrderOverviewValidTariffPriceColor: string;
  AssignmentOrderOverviewValidVoucherCodeColor: string;
  AssignmentOrderOverviewValidTextColor: string;
  AssignmentOrderOverviewInvalidBgColor: string;
  AssignmentOrderOverviewInvalidBorderColor: string;
  AssignmentOrderOverviewInvalidSelectedBorderColor: string;
  AssignmentOrderOverviewInvalidIconColor: string;
  AssignmentOrderOverviewInvalidTariffTypeColor: string;
  AssignmentOrderOverviewInvalidTariffNameColor: string;
  AssignmentOrderOverviewInvalidTariffPriceColor: string;
  AssignmentOrderOverviewInvalidVoucherCodeColor: string;
  AssignmentOrderOverviewInvalidTextColor: string;
  AssignmentOrderOverviewNotAssignedBgColor: string;
  AssignmentOrderOverviewNotAssignedBorderColor: string;
  AssignmentOrderOverviewNotAssignedSelectedBdColor: string;
  AssignmentOrderOverviewNotAssignedIconColor: string;
  AssignmentOrderOverviewNotAssignedTariffTypeColor: string;
  AssignmentOrderOverviewNotAssignedTariffNameColor: string;
  AssignmentOrderOverviewNotAssignedTariffPriceColor: string;
  AssignmentOrderOverviewNotAssignedVoucherCodeColor: string;
  AssignmentOrderOverviewNotAssignedTextColor: string;
  AssignmentOrderOverviewErrorMessageColor: string;
  AssignmentOrderOverviewSubtotalTxColor: string;
  AssignmentOrderOverviewSubtotalPriceColor: string;
  PaginatorColor: string;
  PaginatorActivePageBgColor: string;
}

export type StyleList = Record<StyleListKey, StyleListEntry>;

export type StyleListKey = string;

export interface StyleListEntry {
  inputType?: string;
  list: StyleSelector[];
}

export interface StyleSelector {
  selector: string;
  styles: string;
}

export type StyleVariables = Record<string, string>;
